import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Save, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const PROVIDERS = ["emergent", "openai", "anthropic", "google"];
const MODELS = ["gpt-5.4", "gpt-5.5", "claude-sonnet-4.6", "gemini-3.1-pro"];

export default function LlmSettings() {
  const { t } = useTranslation(["settings", "integrations", "nav", "common", "errors"]);
  const { tenant, setTenant } = useAuth();
  const [provider, setProvider] = useState(tenant?.settings?.llm_provider || "emergent");
  const [model, setModel] = useState(tenant?.settings?.llm_model || "gpt-5.4");
  const [apiKey, setApiKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const keyConfigured = tenant?.settings?.llm_api_key_configured;

  const onSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { llm_provider: provider, llm_model: model };
      // Only send the key if the user typed one. Empty string == clear.
      if (apiKey !== "") payload.llm_api_key = apiKey;
      const { data } = await api.patch("/tenants/me", payload);
      setTenant(data);
      setApiKey("");
      toast.success(t("settings:llm.saveSuccess"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSubmitting(false);
    }
  };

  const clearKey = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.patch("/tenants/me", { llm_api_key: "" });
      setTenant(data);
      setApiKey("");
      toast.success(t("settings:llm.cleared"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="llm-settings-page">
      <header className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t("nav:sections.settings")}</span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{t("settings:llm.title")}</h1>
        <p className="max-w-xl text-sm text-slate-500">{t("settings:llm.subtitle")}</p>
      </header>

      <form onSubmit={onSave} className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" noValidate>
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
              <KeyRound className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{t("settings:llm.keyLabel")}</p>
              <p className="text-xs text-slate-500">
                {keyConfigured ? t("settings:llm.keyConfiguredHint") : t("settings:llm.keyDefaultHint")}
              </p>
            </div>
          </div>
          {keyConfigured ? (
            <Badge data-testid="llm-key-status" variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.tenant")}
            </Badge>
          ) : (
            <Badge data-testid="llm-key-status" variant="outline" className="border-slate-200 bg-white text-slate-600">
              <AlertCircle className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.platform")}
            </Badge>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("settings:llm.providerLabel")}</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="llm-provider-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => (
                  <SelectItem key={p} value={p}>{t(`settings:tenant.providers.${p}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("settings:llm.modelLabel")}</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="llm-model-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODELS.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("settings:llm.keyLabel")}</Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={keyConfigured ? "••••••••••••" : t("settings:llm.keyPlaceholder")}
            data-testid="llm-key-input"
            className="h-11 font-mono text-sm"
            autoComplete="new-password"
          />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-6">
          {keyConfigured ? (
            <Button type="button" variant="outline" onClick={clearKey} disabled={submitting} data-testid="llm-clear-key" className="border-slate-200 text-slate-700 hover:bg-slate-50">
              {t("settings:llm.clear")}
            </Button>
          ) : <span />}
          <Button type="submit" disabled={submitting} data-testid="llm-save-button" className="bg-slate-900 text-white hover:bg-slate-800">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" strokeWidth={1.75} />{t("common:save")}</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
